import { MutableRefObject, useMemo, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import Handsontable from 'handsontable/base';
import { Settings } from 'handsontable/plugins/contextMenu/contextMenu';

// Types
import { TranslationFunction } from '../../../components/i18n/types';
import { TextAlignment, VerticalAlignment } from '../../../../common/table/CellTypeConstants';
import { isGlobalDebugEnabled } from '../../../debug/debugUtil';
import { debugTableCell, debugTableInstance } from '../utils/tableDebugUtils';
import { updateTableCellTextAlignment, updateTableCellVerticalAlignment } from '../actions/hotTableActions';
import { capitalize } from 'lodash';
import {
    isHotTableTextAlignmentApplied,
    isHotTableVerticalAlignmentApplied,
} from '../utils/hotInstanceUtils/hotTableAlignmentUtils';
import {
    addHotTableColsLeft,
    addHotTableColsRight,
    addHotTableRowsAbove,
    addHotTableRowsBelow,
    isHotTableColRemovalDisabled,
    isHotTableRowRemovalDisabled,
    removeHotTableCols,
    removeHotTableRows,
} from '../utils/hotInstanceUtils/hotTableGridOperationsUtils';
import { getNewTransactionId } from '../../../utils/undoRedo/undoRedoTransactionManager';
import {
    getEndCol,
    getEndRow,
    getFirstSelection,
    getStartCol,
    getStartRow,
    isSelectingMultipleSelections,
    isSelectingRefColHeader,
    isSelectingRefRowHeader,
} from '../utils/tableCellSelectionUtils';
import { getShortcutHtml } from '../utils/tableContextMenuUtils';

const SEPARATOR = '---------';

interface UseTableContextMenuProps {
    elementId: string;
    locale: string;
    hotTableInstanceRef: MutableRefObject<Handsontable | null>;
    translateTableContextMenu: TranslationFunction;
}

const useTableContextMenu = ({
    elementId,
    locale,
    hotTableInstanceRef,
    translateTableContextMenu,
}: UseTableContextMenuProps) => {
    const dispatch = useDispatch();

    const dispatchUpdateTableCellTextAlignment = useCallback(
        (textAlignment) => () => {
            dispatch(
                updateTableCellTextAlignment({
                    hotInstance: hotTableInstanceRef.current,
                    elementId,
                    textAlignment,
                }),
            );
        },
        [elementId, hotTableInstanceRef.current],
    );

    const dispatchUpdateTableCellVerticalAlignment = useCallback(
        (verticalAlignment) => () => {
            dispatch(
                updateTableCellVerticalAlignment({
                    hotInstance: hotTableInstanceRef.current,
                    elementId,
                    verticalAlignment,
                }),
            );
        },
        [elementId, hotTableInstanceRef.current],
    );

    const getTableContextMenuConfig = useMemo((): Settings => {
        const getAddColsLeftMenuName = () => {
            const hotCurrentCellSelections = hotTableInstanceRef.current?.getSelected();

            const firstCellSelection = getFirstSelection(hotCurrentCellSelections);

            const isSingleCol =
                firstCellSelection &&
                (getStartCol(firstCellSelection) === getEndCol(firstCellSelection) ||
                    isSelectingRefRowHeader(firstCellSelection));

            const labelSingle = translateTableContextMenu('addColumnLeft_one', 'Insert column left');
            const labelPlural = translateTableContextMenu('addColumnLeft_other', 'Insert columns left');
            const label = isSingleCol ? labelSingle : labelPlural;
            return `${label} ${getShortcutHtml(['alt', '←'])}`;
        };

        const getAddColsRightMenuName = () => {
            const hotCurrentCellSelections = hotTableInstanceRef.current?.getSelected();

            const firstCellSelection = getFirstSelection(hotCurrentCellSelections);

            const isSingleCol =
                firstCellSelection &&
                (getStartCol(firstCellSelection) === getEndCol(firstCellSelection) ||
                    isSelectingRefRowHeader(firstCellSelection));

            const labelSingle = translateTableContextMenu('addColumnRight_one', 'Insert column right');
            const labelPlural = translateTableContextMenu('addColumnRight_other', 'Insert columns right');
            const label = isSingleCol ? labelSingle : labelPlural;
            return `${label} ${getShortcutHtml(['alt', '→'])}`;
        };

        const getRemoveColsMenuName = () => {
            const hotCurrentCellSelections = hotTableInstanceRef.current?.getSelected();

            const firstCellSelection = getFirstSelection(hotCurrentCellSelections);

            const isSingleCol =
                firstCellSelection &&
                (getStartCol(firstCellSelection) === getEndCol(firstCellSelection) ||
                    isSelectingRefRowHeader(firstCellSelection));

            const labelSingle = translateTableContextMenu('removeColumn_one', 'Remove column');
            const labelPlural = translateTableContextMenu('removeColumn_other', 'Remove columns');
            return isSingleCol ? labelSingle : labelPlural;
        };

        const getAddRowsAboveMenuName = () => {
            const hotCurrentCellSelections = hotTableInstanceRef.current?.getSelected();

            const firstCellSelection = getFirstSelection(hotCurrentCellSelections);

            const isSingleRow =
                firstCellSelection &&
                (getStartRow(firstCellSelection) === getEndRow(firstCellSelection) ||
                    isSelectingRefColHeader(firstCellSelection));

            const labelSingle = translateTableContextMenu('addRowAbove_one', 'Insert row above');
            const labelPlural = translateTableContextMenu('addRowAbove_other', 'Insert rows above');
            const label = isSingleRow ? labelSingle : labelPlural;
            return `${label} ${getShortcutHtml(['alt', '↑'])}`;
        };

        const getAddRowsBelowMenuName = () => {
            const hotCurrentCellSelections = hotTableInstanceRef.current?.getSelected();

            const firstCellSelection = getFirstSelection(hotCurrentCellSelections);

            const isSingleRow =
                firstCellSelection &&
                (getStartRow(firstCellSelection) === getEndRow(firstCellSelection) ||
                    isSelectingRefColHeader(firstCellSelection));

            const labelSingle = translateTableContextMenu('addRowBelow_one', 'Insert row below');
            const labelPlural = translateTableContextMenu('addRowBelow_other', 'Insert rows below');
            const label = isSingleRow ? labelSingle : labelPlural;
            return `${label} ${getShortcutHtml(['alt', '↓'])}`;
        };

        const getRemoveRowsMenuName = () => {
            const hotCurrentCellSelections = hotTableInstanceRef.current?.getSelected();

            const firstCellSelection = getFirstSelection(hotCurrentCellSelections);

            const isSingleRow =
                firstCellSelection &&
                (getStartRow(firstCellSelection) === getEndRow(firstCellSelection) ||
                    isSelectingRefColHeader(firstCellSelection));

            const labelSingle = translateTableContextMenu('removeRow_one', 'Remove row');
            const labelPlural = translateTableContextMenu('removeRow_other', 'Remove rows');
            return isSingleRow ? labelSingle : labelPlural;
        };

        const getTextAlignmentMenuName = (textAlignment: TextAlignment) => () => {
            const isTextAlignmentApplied = isHotTableTextAlignmentApplied(
                hotTableInstanceRef.current,
                textAlignment,
                locale,
            );

            const label = translateTableContextMenu(`align${capitalize(textAlignment)}`, `Align ${textAlignment}`);
            return `${label}${isTextAlignmentApplied ? '<span class="tick">✔︎</span>' : ''}`;
        };

        const getVerticalAlignmentMenuName = (verticalAlignment: VerticalAlignment) => () => {
            const isVerticalAlignmentApplied = isHotTableVerticalAlignmentApplied(
                hotTableInstanceRef.current,
                verticalAlignment,
            );

            const label = translateTableContextMenu(
                `align${capitalize(verticalAlignment)}`,
                `Align ${verticalAlignment}`,
            );
            return `${label}${isVerticalAlignmentApplied ? '<span class="tick">✔︎</span>' : ''}`;
        };

        const isTableSelectingMultipleSelections = () =>
            isSelectingMultipleSelections(hotTableInstanceRef.current?.getSelected());

        return {
            items: {
                cut: {
                    name: () => translateTableContextMenu('cut', 'Cut'),
                },
                copy: {
                    name: () => translateTableContextMenu('copy', 'Copy'),
                },
                paste: {
                    key: 'paste',
                    name: () => translateTableContextMenu('paste', 'Paste'),
                    callback() {
                        navigator.clipboard.readText().then((clipboard) => {
                            const copyPastePlugin =
                                hotTableInstanceRef.current &&
                                hotTableInstanceRef.current.getPlugin<'copyPaste'>('copyPaste');
                            copyPastePlugin?.paste(clipboard);
                        });
                    },
                },
                separator4: { name: SEPARATOR },
                add_rows_above_custom: {
                    name: getAddRowsAboveMenuName,
                    disabled: isTableSelectingMultipleSelections,
                    callback: () => addHotTableRowsAbove(hotTableInstanceRef.current, undefined, getNewTransactionId()),
                },
                add_rows_below_custom: {
                    name: getAddRowsBelowMenuName,
                    disabled: isTableSelectingMultipleSelections,
                    callback: () => addHotTableRowsBelow(hotTableInstanceRef.current, undefined, getNewTransactionId()),
                },
                separator1: { name: SEPARATOR },
                add_cols_left_custom: {
                    name: getAddColsLeftMenuName,
                    disabled: isTableSelectingMultipleSelections,
                    callback: () => addHotTableColsLeft(hotTableInstanceRef.current, undefined, getNewTransactionId()),
                },
                add_cols_right_custom: {
                    name: getAddColsRightMenuName,
                    disabled: isTableSelectingMultipleSelections,
                    callback: () => addHotTableColsRight(hotTableInstanceRef.current, undefined, getNewTransactionId()),
                },
                separator2: { name: SEPARATOR },
                remove_rows_custom: {
                    name: getRemoveRowsMenuName,
                    disabled: () => isHotTableRowRemovalDisabled(hotTableInstanceRef.current),
                    callback: () => removeHotTableRows(hotTableInstanceRef.current, undefined, getNewTransactionId()),
                },
                remove_cols_custom: {
                    name: getRemoveColsMenuName,
                    disabled: () => isHotTableColRemovalDisabled(hotTableInstanceRef.current),
                    callback: () => removeHotTableCols(hotTableInstanceRef.current, undefined, getNewTransactionId()),
                },
                separator3: { name: SEPARATOR },
                custom_alignment: {
                    name: () => translateTableContextMenu('changeAlignment', 'Change Alignment...'),
                    submenu: {
                        items: [
                            {
                                key: 'custom_alignment:left',
                                name: getTextAlignmentMenuName(TextAlignment.LEFT),
                                callback: dispatchUpdateTableCellTextAlignment(TextAlignment.LEFT),
                            },
                            {
                                key: 'custom_alignment:center',
                                name: getTextAlignmentMenuName(TextAlignment.CENTER),
                                callback: dispatchUpdateTableCellTextAlignment(TextAlignment.CENTER),
                            },
                            {
                                key: 'custom_alignment:right',
                                name: getTextAlignmentMenuName(TextAlignment.RIGHT),
                                callback: dispatchUpdateTableCellTextAlignment(TextAlignment.RIGHT),
                            },
                            { key: 'separator', name: SEPARATOR },
                            {
                                key: 'custom_alignment:top',
                                name: getVerticalAlignmentMenuName(VerticalAlignment.TOP),
                                callback: dispatchUpdateTableCellVerticalAlignment(VerticalAlignment.TOP),
                            },
                            {
                                key: 'custom_alignment:middle',
                                name: getVerticalAlignmentMenuName(VerticalAlignment.MIDDLE),
                                callback: dispatchUpdateTableCellVerticalAlignment(VerticalAlignment.MIDDLE),
                            },
                            {
                                key: 'custom_alignment:bottom',
                                name: getVerticalAlignmentMenuName(VerticalAlignment.BOTTOM),
                                callback: dispatchUpdateTableCellVerticalAlignment(VerticalAlignment.BOTTOM),
                            },
                        ],
                    },
                },
                ...(isGlobalDebugEnabled()
                    ? {
                          separator5: { name: SEPARATOR },

                          debugTableInstance: {
                              name: () => 'Debug Table Instance',
                              callback: debugTableInstance,
                              hidden: !isGlobalDebugEnabled(),
                          },
                          debugCell: {
                              name: () => 'Debug Cell',
                              callback: debugTableCell(elementId),
                              hidden: !isGlobalDebugEnabled(),
                          },
                      }
                    : {}),
            },
        };
    }, [elementId, locale, hotTableInstanceRef, translateTableContextMenu]);

    return {
        getTableContextMenuConfig,
    };
};

export default useTableContextMenu;
